import { type Set as ISet, Record } from '@gitbook/util-immutable';

import { MODEL_TYPES } from '../constants';
import type { Block, BlockCreateProps } from './block';
import type { BlockRangeCreateProps } from './block-range';
import type { Document } from './document';
import type { Fragment } from './fragment';
import type { Inline, InlineCreateProps } from './inline';
import type { Path } from './lookup-index';
import type { Mark } from './mark';
import { createNodeProperties } from './node-properties';
import type { Text, TextCreateProps } from './text';
import type { TextRangeCreateProps } from './text-range';
import type { SelectionRange } from './value';

const DEFAULTS: {
    length: undefined | number;
    mark: undefined | Mark;
    marks: undefined | ISet<Mark>;
    newPath: undefined | Path;
    node: undefined | Block | Inline | Text;
    offset: undefined | number;
    path: undefined | Path;
    fragmentPath: undefined | Path;
    fragmentIndex: undefined | number;
    position: undefined | number;
    properties: undefined | BlockCreateProps | InlineCreateProps | TextCreateProps;
    selection: undefined | SelectionRange;
    target: undefined | number | null;
    text: undefined | string;
    type: undefined | OperationType;
    data: undefined | any;
    dataPath: undefined | string[];
    dataIndex: undefined | number;
    previousData: undefined | any;
} = {
    length: undefined,
    mark: undefined,
    marks: undefined,
    newPath: undefined,
    node: undefined,
    offset: undefined,
    path: undefined,
    fragmentPath: undefined,
    fragmentIndex: undefined,
    position: undefined,
    properties: undefined,
    selection: undefined,
    target: undefined,
    text: undefined,
    type: undefined,
    data: undefined,
    dataPath: undefined,
    dataIndex: undefined,
    previousData: undefined,
};

/**
 * The side at which an operation must be inserted.
 * @description
 * Used when transforming two "insert_*" operations that are attempting to insert text or
 * nodes at the same offset. It becomes mandatory to know in which order they should be applied.
 */
export type OperationSide = 'left' | 'right';

export type OperationType =
    // Texts
    | 'insert_text'
    | 'remove_text'
    // Marks
    | 'add_mark'
    | 'remove_mark'
    // Nodes
    | 'insert_node'
    | 'remove_node'
    | 'move_node'
    | 'set_node'
    | 'split_node'
    | 'merge_node'
    // Fragments
    | 'insert_fragment'
    | 'set_fragment'
    | 'remove_fragment'
    // Data
    | 'insert_array_data'
    | 'set_data'
    | 'remove_array_data'
    | 'remove_data'
    // Selection
    | 'set_selection'
    // Only used to manually set the entire document value during dev
    | 'set_document';

export type InsertTextOperation = {
    type: 'insert_text';
    path: Path;
    offset: number;
    text: string;
    marks: ISet<Mark>;
};

export type RemoveTextOperation = {
    type: 'remove_text';
    path: Path;
    offset: number;
    text: string;
    marks: ISet<Mark>;
};

export type TextOperation = InsertTextOperation | RemoveTextOperation;

export type AddMarkOperation = {
    type: 'add_mark';
    path: Path;
    offset: number;
    length: number;
    mark: Mark;
};

export type RemoveMarkOperation = {
    type: 'remove_mark';
    path: Path;
    offset: number;
    length: number;
    mark: Mark;
};

export type MarkOperation = AddMarkOperation | RemoveMarkOperation;

export type InsertNodeOperation = {
    type: 'insert_node';
    path: Path;
    node: Block | Inline | Text;
};

export type RemoveNodeOperation = {
    type: 'remove_node';
    path: Path;
    node: Block | Inline | Text;
};

export type MoveNodeOperation = {
    type: 'move_node';
    path: Path;
    newPath: Path;
};

export type SetNodeOperation = {
    type: 'set_node';
    path: Path;
    node: Block | Inline | Text;
    properties: BlockCreateProps | InlineCreateProps | TextCreateProps;
};

export type MergeNodeOperation = {
    type: 'merge_node';
    path: Path;
    position: number;
    properties: BlockCreateProps | InlineCreateProps | TextCreateProps;
    target?: number | null;
};

export type SplitNodeOperation = {
    type: 'split_node';
    path: Path;
    position: number;
    properties: BlockCreateProps | InlineCreateProps | TextCreateProps;
    target?: number | null;
};

export type NodeOperation =
    | InsertNodeOperation
    | RemoveNodeOperation
    | MoveNodeOperation
    | SetNodeOperation
    | MergeNodeOperation
    | SplitNodeOperation;

export type InsertFragmentOperation = {
    type: 'insert_fragment';
    /** Path of the fragment's parent node */
    path: Path;
    /** Absolute path of the fragment. */
    fragmentPath: Path;
    node: Fragment;
    /** Index in the parent's fragments array to insert at. */
    fragmentIndex: number;
};

export type SetFragmentOperation = {
    type: 'set_fragment';
    /** Path of the fragment's parent node */
    path: Path;
    /** Absolute path of the fragment */
    fragmentPath: Path;
    node: Fragment;
    /** Index in the parent's fragments array to change */
    fragmentIndex: number;
};

export type RemoveFragmentOperation = {
    type: 'remove_fragment';
    /** Path of the fragment's parent node */
    path: Path;
    /** Absolute path of the fragment */
    fragmentPath: Path;
    node: Fragment;
    /** Index in the parent's fragments array to remove */
    fragmentIndex: number;
};

export type FragmentOperation =
    | InsertFragmentOperation
    | SetFragmentOperation
    | RemoveFragmentOperation;

export type SetDataOperation = {
    type: 'set_data';
    path: Path;
    dataPath: string[];
    data: any;
    previousData: any | undefined;
};

export type RemoveDataOperation = {
    type: 'remove_data';
    path: Path;
    dataPath: string[];
    data: any;
    previousData: any | undefined;
};

export type InsertArrayDataOperation = {
    type: 'insert_array_data';
    path: Path;
    dataPath: string[];
    data: any;
    dataIndex: number;
};

export type RemoveArrayDataOperation = {
    type: 'remove_array_data';
    path: Path;
    dataPath: string[];
    data: any;
    dataIndex: number;
};

export type DataOperation =
    | SetDataOperation
    | RemoveDataOperation
    | InsertArrayDataOperation
    | RemoveArrayDataOperation;

export type SetSelectionOperation = {
    type: 'set_selection';
    selection: SelectionRange;
    properties: BlockRangeCreateProps | TextRangeCreateProps;
};

export type SetDocumentOperation = {
    type: 'set_document';
    data: Document;
};

export type AnyOperation =
    | TextOperation
    | MarkOperation
    | NodeOperation
    | FragmentOperation
    | DataOperation
    | SetSelectionOperation
    | SetDocumentOperation;

/**
 * Low-level operation on content.
 */
export class Operation extends Record(DEFAULTS) {
    public declare type: OperationType;

    /**
     * Create an operation.
     */
    static create(input: AnyOperation): Operation {
        switch (input.type) {
            case 'merge_node':
            case 'split_node':
                return Operation.createRecord({
                    ...input,
                    properties: createNodeProperties(input.properties),
                });
            case 'set_node':
                return Operation.createRecord({
                    ...input,
                    properties: createNodeProperties(input.properties),
                });
            default:
                return Operation.createRecord(input);
        }
    }

    /**
     * Check if `input` is a `Operation`.
     */
    public static isOperation(input: any): input is Operation {
        return !!input?.[MODEL_TYPES.OPERATION];
    }

    /*
     * Object.
     */
    get object(): 'operation' {
        return 'operation';
    }
}

/*
 * Attach a pseudo-symbol for type checking.
 */

Operation.prototype[MODEL_TYPES.OPERATION] = true;
